import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { api, getErrorMessage } from "../../../../api/client.ts";
import type { Connection, Provider, Tenant } from "../../../../api/client.ts";
import CrmProviderLogo from "../../../../components/CrmProviderLogo.tsx";

// Mounted only while open, so each opening starts blank.
interface AddCrmConnectionDialogProps {
  providers: Provider[] | undefined;
  tenants: Tenant[] | undefined;
  onClose: () => void;
  // mappingError is set when the connection was created but mapping it to the
  // tenant failed; the caller then lets the user map it by hand.
  onCreated: (connection: Connection, mappingError: string | null) => void;
}

/**
 * Connect a CRM and map it to one of the organization's tenants in one go.
 *
 * The organization's connection list only shows connections mapped to its
 * tenants, so a connection created without a mapping would silently vanish.
 */
export default function AddCrmConnectionDialog({
  providers,
  tenants,
  onClose,
  onCreated,
}: AddCrmConnectionDialogProps) {
  const [name, setName] = useState("");
  const [providerId, setProviderId] = useState("");
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [tenantId, setTenantId] = useState("");

  // With a single provider (HubSpot today) there is nothing to choose.
  const selectedId = providerId || (providers?.length === 1 ? providers[0].id : "");
  const provider = providers?.find((p) => p.id === selectedId);

  const create = useMutation({
    mutationFn: async () => {
      const connection = await api.createConnection({
        provider: selectedId,
        name: name.trim() || undefined,
        credentials,
        config: {},
      });
      try {
        await api.createConnectionMapping(connection.id, { tenant_id: tenantId });
        return { connection, mappingError: null };
      } catch (err) {
        return { connection, mappingError: getErrorMessage(err) };
      }
    },
    onSuccess: ({ connection, mappingError }) => onCreated(connection, mappingError),
  });

  function close() {
    if (!create.isPending) onClose();
  }

  const missingCredential = provider?.credential_fields.some(
    (f) => f.required && !credentials[f.name]?.trim(),
  );
  const canSubmit = !!provider && !!tenantId && !missingCredential;

  return (
    <Dialog open onClose={close} maxWidth="sm" fullWidth>
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit && !create.isPending) create.mutate();
        }}
      >
        <DialogTitle>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            {provider && <CrmProviderLogo provider={provider.id} />}
            <span>{provider ? `Connect ${provider.name}` : "Connect a CRM"}</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {(providers?.length ?? 0) > 1 && (
              <ToggleButtonGroup
                exclusive
                value={selectedId}
                onChange={(_, v: string | null) => {
                  if (v === null) return;
                  setProviderId(v);
                  setCredentials({});
                }}
                sx={{ flexWrap: "wrap" }}
              >
                {providers?.map((p) => (
                  <ToggleButton key={p.id} value={p.id} sx={{ gap: 1, textTransform: "none" }}>
                    <CrmProviderLogo provider={p.id} fontSize="small" />
                    {p.name}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
            {provider && (
              <>
                <TextField
                  label="Name"
                  size="small"
                  placeholder={`e.g. Production ${provider.name}`}
                  helperText="Optional. Helps tell connections apart."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
                {provider.credential_fields.map((field) => (
                  <TextField
                    key={field.name}
                    label={field.label}
                    type={field.field_type}
                    size="small"
                    required={field.required}
                    helperText={field.help_text || undefined}
                    value={credentials[field.name] ?? ""}
                    onChange={(e) =>
                      setCredentials((prev) => ({ ...prev, [field.name]: e.target.value }))
                    }
                    autoComplete="off"
                  />
                ))}
                {tenants && tenants.length === 0 ? (
                  <Alert severity="info">
                    This organization has no tenants yet. Create one under Tenants first; a CRM
                    connection syncs into a tenant.
                  </Alert>
                ) : (
                  <TextField
                    select
                    label="Tenant"
                    size="small"
                    required
                    helperText="Where this CRM's companies, contacts and deals sync to. Narrow it to a pipeline later under Tenants."
                    value={tenantId}
                    onChange={(e) => setTenantId(e.target.value)}
                  >
                    {tenants?.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </>
            )}
            {!provider && (providers?.length ?? 0) > 1 && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Choose the CRM to connect.
              </Typography>
            )}
            {create.isError && <Alert severity="error">{getErrorMessage(create.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!canSubmit} loading={create.isPending}>
            Connect
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
